import React, { useState, useContext } from "react"
import { AlertsContext } from "../../../utils/providers/alerts"
import { funEU } from "../../../utils/firebase"

import Window from "../../structure/window/window"
import WindowCard from "../../ui/window-card/window-card"
import Input from "../../ui/inputs/input"
import Textarea from "../../ui/inputs/textarea"
import Button from "../../ui/button/button"
import RecipientPicker from "../recipient-picker/recipient-picker"

const refundChargeCallable = funEU.httpsCallable("refundCharge")

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const formatGbp = (value) =>
	Number(value || 0).toLocaleString("en-GB", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})

/**
 * Refund modal. Admin / super-admin only (gated on the row that opens it,
 * and again server-side in the callable).
 *
 * Defaults to the full remaining-refundable amount. Reason is required.
 * Recipient picker decides where the branded refund confirmation email
 * goes; it CC's the sales agent automatically inside the webhook.
 */
export default function Refund({ bookingID, paymentID, originalAmount, refundedAlready, originalReference, leadBooker, close }) {
	const { pushAlert } = useContext(AlertsContext)

	const remaining = Math.max(Number(originalAmount || 0) - Number(refundedAlready || 0), 0)
	const remainingRounded = Math.round(remaining * 100) / 100

	const [amount, setAmount] = useState(remainingRounded > 0 ? remainingRounded.toFixed(2) : "")
	const [reason, setReason] = useState("")
	const [useLeadBooker, setUseLeadBooker] = useState(Boolean(leadBooker?.email))
	const [alternateEmail, setAlternateEmail] = useState("")
	const [sending, setSending] = useState(false)
	const [errors, setErrors] = useState({})

	const handleAmountChange = (raw) => {
		if (raw === "" || /^\d*\.?\d{0,2}$/.test(raw)) {
			setAmount(raw)
		}
	}

	const handleSetToFull = () => {
		setAmount(remainingRounded.toFixed(2))
	}

	const validate = () => {
		const nextErrors = {}

		const amountNum = Number(amount)
		if (!amount.trim()) {
			nextErrors.amount = "Amount is required."
		} else if (!Number.isFinite(amountNum)) {
			nextErrors.amount = "Amount must be a number."
		} else if (amountNum <= 0) {
			nextErrors.amount = "Amount must be greater than zero."
		} else if (amountNum > remainingRounded + 0.001) {
			nextErrors.amount = `Amount can't exceed £${formatGbp(remainingRounded)} remaining.`
		}

		if (!reason.trim()) {
			nextErrors.reason = "A refund reason is required."
		} else if (reason.length > 500) {
			nextErrors.reason = "Reason is too long (max 500 characters)."
		}

		if (useLeadBooker) {
			if (!leadBooker?.email) {
				nextErrors.recipient = "Lead booker has no email on file. Untick and enter an address."
			}
		} else {
			const trimmed = alternateEmail.trim()
			if (!trimmed) {
				nextErrors.recipient = "Recipient email is required."
			} else if (!EMAIL_RE.test(trimmed)) {
				nextErrors.recipient = "That doesn't look like a valid email address."
			}
		}

		setErrors(nextErrors)
		return Object.keys(nextErrors).length === 0
	}

	const submit = async () => {
		if (!validate()) return

		setSending(true)

		try {
			await refundChargeCallable({
				bookingID,
				paymentID,
				amount: Number(amount),
				reason: reason.trim(),
				useLeadBooker,
				alternateEmail: useLeadBooker ? undefined : alternateEmail.trim(),
			})

			pushAlert({
				type: "SUCCESS",
				title: "Refund issued",
			})
			close()
		} catch (err) {
			const message = err?.message || "Could not issue refund."
			pushAlert({
				type: "ERROR",
				title: message,
			})
			setSending(false)
		}
	}

	return (
		<Window
			title="Issue a refund"
			className="slim"
			close={() => close()}>
			<WindowCard>
				<div className="input-grid cols-2">
					<div className="span-2">
						<p style={{ fontSize: 12, color: "#667085", marginBottom: 4 }}>Refunding against payment</p>
						<p style={{ fontSize: 13, color: "#101828", fontWeight: 500 }}>{originalReference || paymentID}</p>
					</div>

					<div className="span-2">
						<div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
							<div style={{ flex: 1 }}>
								<Input
									type="text"
									inputMode="decimal"
									placeholder="Refund amount:"
									symbol="£"
									value={amount}
									onChange={handleAmountChange}
									error={errors.amount}
								/>
							</div>
							<Button
								label="Set to full"
								small={true}
								onClick={handleSetToFull}
								disabled={remainingRounded <= 0}
							/>
						</div>
						<p style={{ fontSize: 11, color: "#667085", marginTop: 4 }}>Remaining refundable: £{formatGbp(remainingRounded)}</p>
						{errors.amount && <p style={{ fontSize: 12, color: "#b42318", marginTop: 4 }}>{errors.amount}</p>}
					</div>

					<div className="span-2">
						<Textarea
							type="text"
							placeholder="Reason for refund (visible internally and on the client receipt):"
							value={reason}
							onChange={setReason}
						/>
						{errors.reason && <p style={{ fontSize: 12, color: "#b42318", marginTop: 4 }}>{errors.reason}</p>}
					</div>

					<div className="span-2">
						<RecipientPicker
							leadBookerEmail={leadBooker?.email}
							leadBookerLabel={leadBooker?.email}
							useLeadBooker={useLeadBooker}
							onToggle={(next) => {
								setUseLeadBooker(next)
								setErrors((e) => ({ ...e, recipient: undefined }))
							}}
							alternateEmail={alternateEmail}
							onAlternateChange={(value) => {
								setAlternateEmail(value)
								setErrors((e) => ({ ...e, recipient: undefined }))
							}}
							error={errors.recipient}
							checkboxLabel="Send refund confirmation to lead booker"
						/>
					</div>

					<div className="span-2">
						<Button
							label={`Refund £${formatGbp(Number(amount) || 0)}`}
							fullWidth={true}
							loading={sending}
							loadingText="Refunding..."
							onClick={submit}
							disabled={remainingRounded <= 0}
						/>
					</div>
				</div>
			</WindowCard>
		</Window>
	)
}
